import { Session } from "@/pages/sessions/types/session";

// "Dr. Asha Rao" ~ "asha rao" — lowercase, drop titles, flatten punctuation
const normalizeName = (name: string) =>
  name
    .toLowerCase()
    .replace(/\b(dr|mr|mrs|ms|prof)\b\.?/g, " ")
    .replace(/[^a-z0-9]+/g, " ")
    .trim();

/**
 * Calendly reports canceler_type 'host' for the professional, the care team,
 * and CRM cancels alike — canceled_by (a display name, sometimes an email) is
 * the only discriminator, matched against the professional's emails and name.
 * Fails safe: no match → false (a wrong "by professional" misdirects CX; a
 * miss merely under-reports).
 */
export function isCancelledByProfessional(session: Session): boolean {
  const { canceled_by, canceler_type, professional } = session;
  if (canceler_type !== "host" || !canceled_by || !professional) {
    return false;
  }

  const professionalEmails = [professional.email, professional.personal_email]
    .filter((email): email is string => Boolean(email))
    .map((email) => email.trim().toLowerCase());
  if (professionalEmails.includes(canceled_by.trim().toLowerCase())) {
    return true;
  }

  if (!professional.name) return false;
  // non-Latin names normalize to "" — an empty string must never match
  const normalizedCanceledBy = normalizeName(canceled_by);
  return (
    normalizedCanceledBy !== "" &&
    normalizedCanceledBy === normalizeName(professional.name)
  );
}

export interface CancellationBadgeContent {
  label: string;
  className: string;
}

/**
 * Shared by the session card and the session detail header so the two sites
 * cannot drift. Returns one pill per fact, rendered side by side:
 * status (Cancelled red / Rescheduled orange), timing from the stored
 * canceled_within_24h (<24h amber — a fee applied — / >24h blue; omitted on
 * unclassified pre-backfill rows), and By professional (purple) derived from
 * canceled_by/canceler_type.
 */
export function getCancellationBadges(
  session: Session,
): CancellationBadgeContent[] {
  const badges: CancellationBadgeContent[] = [
    session.is_rescheduled
      ? { label: "Rescheduled", className: "bg-orange-500 text-white border-0" }
      : { label: "Cancelled", className: "bg-red-500 text-white border-0" },
  ];

  if (session.canceled_within_24h === true) {
    badges.push({
      label: "<24h",
      className: "bg-amber-500 text-white border-0",
    });
  } else if (session.canceled_within_24h === false) {
    badges.push({
      label: ">24h",
      className: "bg-blue-500 text-white border-0",
    });
  }

  if (isCancelledByProfessional(session)) {
    badges.push({
      label: "By professional",
      className: "bg-purple-600 text-white border-0",
    });
  }

  return badges;
}

/**
 * "Actioned by" name for a CRM cancel: the resolved internal user's full name,
 * falling back to their email. Null when the cancel predates the column or was
 * not made from the CRM (patient, professional, or the reschedule flow).
 * Shared by the session card and the session detail page so they cannot drift.
 */
export function getCancelActionedByName(session: Session): string | null {
  const actor = session.cancel_actioned_by;
  if (!actor) return null;
  const name = [actor.first_name, actor.last_name]
    .map((part) => part?.trim())
    .filter(Boolean)
    .join(" ");
  return name || actor.email || null;
}
